import { createColumnHelper } from "@tanstack/react-table";
import { T, useTranslate } from "@tolgee/react";
import type { TradePerpMarketSummary } from "../../hooks/use-trade-data.ts";
import {
	formatFundingRate,
	formatPrice,
	formatSignedPercent,
} from "../../lib/formatters.ts";
import { getLocalizedMarketName } from "../../lib/market-display.ts";

function formatCompactUsdMetric(value: number) {
	return new Intl.NumberFormat("en-US", {
		notation: "compact",
		maximumFractionDigits: value >= 1_000_000_000 ? 2 : 1,
	}).format(value);
}

const col = createColumnHelper<TradePerpMarketSummary>();

function MarketSubtitle({ coin, dex }: { coin: string; dex?: string }) {
	const { t } = useTranslate();
	return getLocalizedMarketName({ coin, dex, t });
}

export const perpMarketColumns = [
	col.accessor("displaySymbol", {
		header: () => <T keyName="market.columns.symbol" />,
		enableSorting: false,
		cell: ({ row }) => (
			<div className="min-w-0">
				<div className="flex items-start gap-2">
					<div className="truncate text-label2-bold text-trade-text-strong">
						{row.original.displaySymbol}
					</div>
					{/* ponytail: caption2(11px)가 최소 토큰이라 10px는 one-off — leading 14px 유지로 태그 높이 18px = 심볼 줄 높이 = ROW_HEIGHT(38px) 정합 */}
					<span className="shrink-0 rounded-full bg-trade-interactive px-1.5 py-0.5 font-medium text-[10px] leading-[14px] text-trade-text-secondary">
						{row.original.maxLeverage}x
					</span>
				</div>
				<div className="truncate text-caption1-medium text-trade-text-faint text-trade-text-secondary">
					<MarketSubtitle coin={row.original.coin} dex={row.original.dex} />
				</div>
			</div>
		),
	}),
	col.accessor("markPrice", {
		header: () => <T keyName="market.columns.last-price" />,
		cell: ({ getValue }) => (
			<span className="truncate text-label2-medium text-trade-text-strong">
				{formatPrice(getValue())}
			</span>
		),
	}),
	col.accessor("priceChangePercent", {
		header: () => <T keyName="market.columns.24h-change" />,

		cell: ({ getValue }) => {
			const value = getValue();
			return (
				<span
					className={`truncate text-label2-medium ${value >= 0 ? "text-trade-green" : "text-trade-red"}`}
				>
					{formatSignedPercent(value)}
				</span>
			);
		},
	}),
	col.accessor("predictedFundingRate", {
		header: () => <T keyName="market.columns.funding" />,

		cell: ({ getValue }) => {
			const value = getValue();
			return (
				<span
					className={`truncate text-label2-medium ${value >= 0 ? "text-trade-green" : "text-trade-red"}`}
				>
					{formatFundingRate(value)}
				</span>
			);
		},
	}),
	col.accessor("dayNotionalVolume", {
		header: () => <T keyName="market.columns.volume" />,

		cell: ({ getValue }) => (
			<span className="truncate text-label2-medium text-trade-text-strong">
				${formatCompactUsdMetric(getValue())}
			</span>
		),
	}),
	col.accessor("openInterestValue", {
		header: () => <T keyName="market.columns.open-interest" />,

		cell: ({ getValue }) => (
			<span className="truncate text-label2-medium text-trade-text-strong">
				${formatCompactUsdMetric(getValue())}
			</span>
		),
	}),
];
